import { isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import {
  useGeneratePreSignedPutUrlMutation,
  useImportCrownTariffMutation,
} from "../../../../generated/graphql";
import type { BulkBinding, BulkRow, BulkRowStatus } from "./bulk-types";

// Mirrors the backend `CROWN_DRY_RUN_UNAVAILABLE_CODE` (crown-data.resolver.ts).
const DRY_RUN_UNAVAILABLE_CODE = "CROWN_DRY_RUN_UNAVAILABLE";

type GraphqlErrorLike = {
  readonly extensions?: { readonly code?: unknown } | null;
};

/**
 * True when a mutation error response carries the stable "dry-run unavailable"
 * code, signalling a fall back to direct-commit mode for that row.
 *
 * @param errors The `errors` array from an Apollo mutation result.
 * @returns Whether any error matches the dry-run-unavailable code.
 */
const hasDryRunUnavailableError = (
  errors: readonly GraphqlErrorLike[] | undefined,
): boolean =>
  errors?.some(
    (error) => error.extensions?.code === DRY_RUN_UNAVAILABLE_CODE,
  ) ?? false;

/**
 * Outcome of a single import attempt (dry-run or commit) against one row.
 */
type RunOutcome =
  | { kind: "ok"; tariffsToCreate: number; rangesToCreate: number }
  | { kind: "dry-run-unavailable" }
  | { kind: "error"; message: string };

/**
 * Summary of a batch run (dry-run-all or import-all) for the run-level Snackbar.
 */
export type RunSummary = {
  action: "dry-run" | "import";
  succeeded: number;
  failed: number;
};

/**
 * Converts an ISO date string (YYYY-MM-DD) to a Date, treating blank/nil as
 * null, matching the wizard's `buildInput` behavior.
 *
 * @param value The ISO date string or null.
 * @returns A Date, or null when empty.
 */
const toDate = (value: string | null): Date | null => {
  if (isNil(value) || value === "") {
    return null;
  }
  return new Date(value);
};

/**
 * Maps a row's binding + upload URL to the `importCrownTariffInput` payload.
 *
 * @param row The bulk row.
 * @param dryRun Whether to request a preview (true) or a commit (false).
 * @returns The mutation input, or null when the row has not been uploaded yet.
 */
const buildInput = (row: BulkRow, dryRun: boolean) => {
  if (isNil(row.tariffUrl)) {
    return null;
  }
  const { binding } = row;
  return {
    contactUuids: binding.contactUuids,
    serviceUuids: binding.serviceUuids,
    vehicleTypeUuids: binding.vehicleTypeUuids,
    name: binding.tariffName,
    tariffZoneType: binding.tariffZoneType,
    tariffType: binding.tariffType,
    rateType: binding.rateType,
    tariffGroupType: binding.tariffGroupType,
    tariffZoneGroupId: binding.tariffZoneGroupId,
    tariffUrl: row.tariffUrl,
    useActualWeight: binding.useActualWeight,
    autoSelectAllServices: binding.autoSelectAllServices,
    autoSelectAllVehicleTypes: binding.autoSelectAllVehicleTypes,
    startDate: toDate(binding.startDate),
    endDate: toDate(binding.endDate),
    fuelProfileUuid: binding.fuelProfileUuid,
    dryRun,
  };
};

/**
 * Derives the default tariff name from a CSV filename: strips the directory,
 * extension, and a leading numeric id prefix (e.g. `001_ch_robinson.csv` ->
 * `ch robinson`).
 *
 * @param fileName The CSV filename.
 * @returns A human-friendly default tariff name.
 */
export const defaultNameFromFile = (fileName: string): string =>
  fileName
    .replace(/\.csv$/i, "")
    .replace(/^\d+[_-]/, "")
    .replaceAll(/[_-]+/g, " ")
    .trim();

/**
 * Generates a stable client-side row id. Prefers `crypto.randomUUID`, falling
 * back to a timestamp+random id when it is unavailable (e.g. a non-secure
 * context) so row creation never throws.
 *
 * @returns A unique row id (not persisted).
 */
export const makeRowId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `row-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/**
 * Orchestrates the bulk tariff import: owns the row list, uploads CSVs to S3 via
 * presigned URLs, and runs sequential dry-run / commit passes against the
 * existing `importCrownTariff` mutation (one call per row, awaited in turn to
 * avoid Apollo batching multiple long transactions into a single request).
 *
 * Per-row isolation: one row's failure never blocks the others. Committed rows
 * are locked (`imported`) for the session because the import engine is not
 * idempotent and re-committing would create duplicate tariff groups.
 *
 * @param companyUuid The logged-in company the tariffs import into (unused in
 *   the payload, which derives company server-side, but kept for clarity/guards).
 * @returns Row state and the actions the bulk UI drives.
 */
export const useBulkTariffImport = (companyUuid: string) => {
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [lastSummary, setLastSummary] = useState<RunSummary | null>(null);

  const [generatePreSignedPutUrl] = useGeneratePreSignedPutUrlMutation();
  const [importCrownTariff] = useImportCrownTariffMutation();

  const patchRow = useCallback(
    (id: string, patch: Partial<BulkRow>) => {
      setRows((previous) =>
        previous.map((row) => (row.id === id ? { ...row, ...patch } : row)),
      );
    },
    [setRows],
  );

  /**
   * Updates the binding of a single row (used by inline grid edits).
   */
  const updateBinding = useCallback(
    (id: string, patch: Partial<BulkBinding>) => {
      setRows((previous) =>
        previous.map((row) =>
          row.id === id
            ? { ...row, binding: { ...row.binding, ...patch } }
            : row,
        ),
      );
    },
    [setRows],
  );

  const removeRow = useCallback(
    (id: string) => {
      setRows((previous) => previous.filter((row) => row.id !== id));
    },
    [setRows],
  );

  /**
   * Uploads one row's file to S3 via a presigned URL and records the URL.
   * Updates the row status to `uploading` then `ready`/`error`.
   */
  const uploadRow = useCallback(
    async (row: BulkRow): Promise<void> => {
      if (isNil(row.file)) {
        return;
      }
      patchRow(row.id, { status: "uploading", message: null });
      try {
        const response = await generatePreSignedPutUrl({
          variables: { filename: row.file.name, filetype: row.file.type },
        });
        const presignedUrl = response.data?.generatePreSignedPutUrl;
        if (isNil(presignedUrl)) {
          patchRow(row.id, {
            status: "error",
            message: "Failed to get an upload URL",
          });
          return;
        }
        await fetch(presignedUrl, {
          method: "PUT",
          body: row.file,
          headers: { "Content-Type": row.file.type },
        });
        patchRow(row.id, { status: "ready", tariffUrl: presignedUrl });
      } catch (error) {
        patchRow(row.id, {
          status: "error",
          message: `Upload failed: ${String(error)}`,
        });
      }
    },
    [generatePreSignedPutUrl, patchRow],
  );

  /**
   * Adds files to the grid and uploads them. A file whose name matches an
   * existing fileless placeholder row (created by loading a manifest before the
   * CSVs) is attached to that row instead of creating a duplicate, so the
   * manifest bindings are preserved. All other files become new rows seeded from
   * the supplied `makeRow`. Uploads run sequentially to avoid flooding the
   * presigned-URL endpoint.
   */
  const addFiles = useCallback(
    (files: File[], makeRow: (file: File) => BulkRow) => {
      const next = [...rows];
      const toUpload: BulkRow[] = [];
      for (const file of files) {
        const placeholderIndex = next.findIndex(
          (row) =>
            row.file === null &&
            isNil(row.tariffUrl) &&
            row.fileName === file.name,
        );
        const placeholder = next[placeholderIndex];
        if (placeholderIndex !== -1 && !isNil(placeholder)) {
          const attached: BulkRow = {
            ...placeholder,
            file,
            status: "pending",
            message: null,
          };
          next[placeholderIndex] = attached;
          toUpload.push(attached);
        } else {
          const row = makeRow(file);
          next.push(row);
          toUpload.push(row);
        }
      }
      setRows(next);
      void (async () => {
        for (const row of toUpload) {
          // eslint-disable-next-line no-await-in-loop
          await uploadRow(row);
        }
      })();
    },
    [rows, setRows, uploadRow],
  );

  /**
   * Runs a single import attempt for a row and returns the typed outcome.
   */
  const runRow = useCallback(
    async (row: BulkRow, dryRun: boolean): Promise<RunOutcome> => {
      const input = buildInput(row, dryRun);
      if (isNil(input)) {
        return { kind: "error", message: "Row has not been uploaded" };
      }
      try {
        const response = await importCrownTariff({
          variables: { importCrownTariffInput: input },
        });
        if (dryRun && hasDryRunUnavailableError(response.errors)) {
          return { kind: "dry-run-unavailable" };
        }
        const result = response.data?.importCrownTariff;
        if (isNil(result)) {
          return {
            kind: "error",
            message: response.errors?.[0]?.message ?? "No response",
          };
        }
        if (!result.success || !isNil(result.error)) {
          return { kind: "error", message: result.error ?? "Import failed" };
        }
        return {
          kind: "ok",
          tariffsToCreate: result.tariffsToCreate ?? 0,
          rangesToCreate: result.rangesToCreate ?? 0,
        };
      } catch (error) {
        return { kind: "error", message: `Error: ${String(error)}` };
      }
    },
    [importCrownTariff],
  );

  /**
   * Applies a run outcome to a row's status/message/counts.
   */
  const applyOutcome = useCallback(
    (id: string, outcome: RunOutcome, committed: boolean) => {
      if (outcome.kind === "ok") {
        patchRow(id, {
          status: committed ? "imported" : "dry-run-ok",
          message: committed
            ? `Imported ${outcome.tariffsToCreate} tariffs / ${outcome.rangesToCreate} ranges`
            : null,
          tariffsToCreate: outcome.tariffsToCreate,
          rangesToCreate: outcome.rangesToCreate,
        });
      } else if (outcome.kind === "dry-run-unavailable") {
        patchRow(id, {
          status: "dry-run-unavailable",
          message: "Preview unavailable (line-haul networks off)",
        });
      } else {
        patchRow(id, { status: "error", message: outcome.message });
      }
    },
    [patchRow],
  );

  /**
   * Sequentially dry-runs every row eligible for preview, then records a
   * run-level summary. Already-imported rows are skipped.
   */
  const dryRunAll = useCallback(async () => {
    setBusy(true);
    let succeeded = 0;
    let failed = 0;
    try {
      const targets = rows.filter(
        (row) => row.status !== "imported" && !isNil(row.tariffUrl),
      );
      for (const row of targets) {
        patchRow(row.id, { status: "running" });
        // eslint-disable-next-line no-await-in-loop
        const outcome = await runRow(row, true);
        applyOutcome(row.id, outcome, false);
        if (outcome.kind === "error") {
          failed += 1;
        } else {
          succeeded += 1;
        }
      }
    } finally {
      setBusy(false);
      setLastSummary({ action: "dry-run", succeeded, failed });
    }
  }, [rows, patchRow, runRow, applyOutcome]);

  /**
   * Sequentially commits every row that has a clean dry-run (or for which
   * preview is unavailable) and is not already imported, then records a summary.
   */
  const importAll = useCallback(async () => {
    setBusy(true);
    let succeeded = 0;
    let failed = 0;
    try {
      const targets = rows.filter(
        (row) =>
          (row.status === "dry-run-ok" ||
            row.status === "dry-run-unavailable") &&
          !isNil(row.tariffUrl),
      );
      for (const row of targets) {
        patchRow(row.id, { status: "running" });
        // eslint-disable-next-line no-await-in-loop
        const outcome = await runRow(row, false);
        applyOutcome(row.id, outcome, true);
        if (outcome.kind === "error") {
          failed += 1;
        } else {
          succeeded += 1;
        }
      }
    } finally {
      setBusy(false);
      setLastSummary({ action: "import", succeeded, failed });
    }
  }, [rows, patchRow, runRow, applyOutcome]);

  const counts = useMemo(() => {
    const importable = rows.filter(
      (row) =>
        row.status === "dry-run-ok" || row.status === "dry-run-unavailable",
    ).length;
    const blocked = rows.filter(
      (row) => row.status === "error" || row.status === "pending",
    ).length;
    const imported = rows.filter((row) => row.status === "imported").length;
    // Surfaced next to the import counts so a manifest problem is visible at the
    // moment of committing, not only in the row that carries it.
    const warned = rows.filter((row) => !isNil(row.manifestWarning)).length;
    return { total: rows.length, importable, blocked, imported, warned };
  }, [rows]);

  return {
    rows,
    setRows,
    busy,
    lastSummary,
    setLastSummary,
    counts,
    companyUuid,
    addFiles,
    removeRow,
    updateBinding,
    dryRunAll,
    importAll,
  };
};
